import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { MoreVertical, Download, Archive, Trash2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseActionsDropdown = ({ caseItem, onUpdate }) => {
  const navigate = useNavigate();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOpenCase = () => {
    navigate(`/case/${caseItem.id}`);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseItem.id}/download/pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Create blob with explicit type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = (caseItem.case_name || caseItem.client_name || 'chronology').replace(/ /g, '_');
      link.setAttribute('download', `${filename}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Chronology downloaded as PDF');
    } catch (error) {
      console.error('Download PDF error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const handleDownloadWord = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseItem.id}/download/docx`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      });
      
      // Create blob with explicit type
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = (caseItem.case_name || caseItem.client_name || 'chronology').replace(/ /g, '_');
      link.setAttribute('download', `${filename}.docx`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Chronology downloaded as Word document');
    } catch (error) {
      console.error('Download Word error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download Word document');
    }
  };

  const handleArchive = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/cases/${caseItem.id}/archive`);
      toast.success('Case archived successfully');
      setShowArchiveDialog(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Archive error:', error);
      toast.error('Failed to archive case');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/cases/${caseItem.id}`);
      toast.success('Case deleted successfully');
      setShowDeleteDialog(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete case');
    } finally {
      setLoading(false);
    }
  };

  const canDownload = caseItem.status === 'completed';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleOpenCase}>
            <FolderOpen className="mr-2 h-4 w-4" />
            Open Case
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDownloadPDF} disabled={!canDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDownloadWord} disabled={!canDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download Word
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowArchiveDialog(true)}>
            <Archive className="mr-2 h-4 w-4" />
            Archive Case
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setShowDeleteDialog(true)}
            style={{ color: '#DC2626' }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Case
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent className="glass-card">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#245451' }}>Archive Case?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this case? You can still view and download it later from the Archived Cases view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={loading}
              style={{ backgroundColor: '#245451' }}
            >
              {loading ? 'Archiving...' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="glass-card">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#DC2626' }}>
              Delete {caseItem.case_name}?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#64748B' }}>
              Are you sure you want to permanently delete this case? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: '#E2E8F0', color: '#64748B' }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading}
              style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CaseActionsDropdown;
